import { useState, useMemo, useCallback } from "react";
import { CompanyAdmin, CompanyAdminInput } from "@/types/crm";

export function useCompaniesAdmin() {
  const [companies, setCompanies] = useState<CompanyAdmin[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleStatusChange = useCallback((status: string) => {
    setStatusFilter(status);
  }, []);

  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const matchesSearch =
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.descripcion?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && company.estado === 1) ||
        (statusFilter === "inactive" && company.estado === 0);

      return matchesSearch && matchesStatus;
    });
  }, [companies, searchQuery, statusFilter]);

  const statistics = useMemo(
    () => ({
      totalCompanies: companies.length,
      activeCompanies: companies.filter((c) => c.estado === 1).length,
      inactiveCompanies: companies.filter((c) => c.estado === 0).length,
      totalUsers: companies.reduce(
        (sum, company) => sum + (company.totalUsuarios || 0),
        0
      ),
      totalOpportunities: companies.reduce(
        (sum, company) => sum + (company.totalOportunidades || 0),
        0
      ),
    }),
    [companies]
  );

  const createCompany = useCallback((companyData: CompanyAdminInput) => {
    const newCompany: CompanyAdmin = {
      id: Date.now().toString(),
      name: companyData.name,
      descripcion: companyData.descripcion,
      logo_url: companyData.logo_url,
      direccion: companyData.direccion,
      telefono: companyData.telefono,
      email: companyData.email,
      sitio_web: companyData.sitio_web,
      estado: 1,
      fecha_creacion: new Date().toISOString(),
      fecha_actualizacion: new Date().toISOString(),
      totalUsuarios: 0,
      totalOportunidades: 0,
    };

    setCompanies((prev) => [...prev, newCompany]);
  }, []);

  const updateCompany = useCallback(
    (id: string, companyData: CompanyAdminInput) => {
      setCompanies((prev) =>
        prev.map((company) =>
          company.id === id
            ? {
                ...company,
                ...companyData,
                fecha_actualizacion: new Date().toISOString(),
              }
            : company
        )
      );
    },
    []
  );

  const deleteCompany = useCallback((company: CompanyAdmin) => {
    setCompanies((prev) =>
      prev.map((c) => (c.id === company.id ? { ...c, estado: 0 } : c))
    );
  }, []);

  return {
    companies,
    filteredCompanies,
    searchQuery,
    setSearchQuery: handleSearchChange,
    statusFilter,
    setStatusFilter: handleStatusChange,
    createCompany,
    updateCompany,
    deleteCompany,
    statistics,
  };
}
